<script setup lang="ts">
import { setUserLanguage } from '@/api/settings'
import { getUser } from '@/api/user'
import BaseDropdown from '@/components/base/BaseDropdown.vue'
import { useLanguageStore } from '@/store/language'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

defineOptions({
  name: 'LocaleSwitcher'
})

const languageStore = useLanguageStore()
const store = useStore()
const { availableLocales } = useI18n()

const { language } = storeToRefs(languageStore)

const isAdmin = computed<boolean>(() => store.getters['companyData/isAdmin'])

const isLoggedIn = computed<boolean>(() => store.getters['auth/isLoggedIn'])

const switchLanguage = async (locale: Locale): Promise<void> => {
  await languageStore.changeLanguage(locale)
  if (isLoggedIn.value && !isAdmin.value) {
    await setUserLanguage(locale)
    const user = await getUser()
    await store.dispatch('companyData/setUserCompanyDetails', user)
  }
}
</script>

<template>
  <BaseDropdown id="locale-switcher" align="center">
    <template #button="{ toggle }">
      <button
        id="locale-switcher"
        type="button"
        class="flex items-center fill-current font-medium uppercase outline-hidden"
        @click="toggle"
      >
        <span class="text-lg font-medium md:text-base">{{ language }}</span>
        <VIcon icon="$chevron-down" size="1rem" />
      </button>
    </template>
    <template #default>
      <button
        v-for="locale in availableLocales"
        :key="locale"
        type="button"
        class="w-full p-2 text-lg uppercase transition duration-150 ease-in-out hover:bg-secondary-100 md:px-4 md:text-sm"
        :class="{ 'active-lang': locale === language }"
        @click="switchLanguage(locale)"
      >
        {{ locale }}
      </button>
    </template>
  </BaseDropdown>
</template>

<style lang="scss" scoped>
@reference "../styles/main.css";

.active-lang {
  @apply bg-primary/10;
}
</style>
